<template>
    <div class="tw-grid tw-grid-cols-12 tw-gap-4">
        <div class="tw-col-span-12 lg:tw-col-span-12">
            <div class="tw-w-full tw-h-full tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-primary-200 tw-min-h-[600px]">
                <!-- Header Section -->
                <div class="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-mb-6">
                    <div>
                        <h1 class="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">Products</h1>
                        <p class="tw-text-sm tw-text-gray-600">{{ paginationData.total }} products total</p>
                    </div>
                    
                    <!-- Header Actions -->
                    <div class="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                        <!-- Search -->
                        <div class="tw-min-w-[250px]">
                            <FormField
                                v-model="searchQuery"
                                type="text"
                                placeholder="Search products..."
                                left-icon="solar:magnifer-linear"
                                hide-label
                                hide-errors
                                class="tw-w-full"
                                @input="handleSearch"
                                :disabled="loading"
                            />
                        </div>
                        
                        <!-- View Toggle -->
                        <div class="tw-flex tw-items-center tw-bg-gray-100 tw-rounded-md tw-p-1">
                            <button
                                @click="viewMode = 'grid'"
                                :disabled="loading"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'grid' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900',
                                    loading ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                                ]"
                            >
                                <icon icon="solar:widget-4-bold-duotone" class="tw-text-lg" />
                            </button>
                            <button
                                @click="viewMode = 'list'"
                                :disabled="loading"
                                :class="[
                                    'tw-px-3 tw-py-1.5 tw-rounded tw-text-sm tw-transition-colors',
                                    viewMode === 'list' ? 'tw-bg-white tw-text-gray-900 tw-shadow-sm' : 'tw-text-gray-600 hover:tw-text-gray-900',
                                    loading ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
                                ]"
                            >
                                <icon icon="solar:list-bold-duotone" class="tw-text-lg" />
                            </button>
                        </div>
                        
                        <!-- Add Product Button -->
                        <a-button
                            as="router-link"
                            :to="{ name: 'pages.company.products.create' }"
                            intent="gradient"
                            left-icon="solar:add-circle-bold-duotone"
                            :disabled="loading"
                        >
                            Add Product
                        </a-button>
                    </div>
                </div>

                <!-- Filters Bar -->
                <div v-if="false" class="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded-lg">
                    <!-- Category Filter -->
                    <FormMultiSelect
                        v-model="selectedCategories"
                        :options="categoryOptions"
                        placeholder="Filter by categories"
                        left-icon="solar:widget-bold-duotone"
                        hide-label
                        hide-errors
                        class="tw-min-w-[200px]"
                    />

                    <!-- Sort -->
                    <FormField
                        v-model="sortBy"
                        type="select"
                        hide-label
                        hide-errors
                        left-icon="solar:sort-horizontal-linear"
                        class="tw-min-w-[150px]"
                    >
                        <option value="name">Sort by Name</option>
                        <option value="date">Sort by Date</option>
                        <option value="categories">Sort by Categories</option>
                    </FormField>

                    <!-- Clear Filters -->
                    <a-button
                        v-if="hasActiveFilters"
                        @click="clearFilters"
                        intent="ghost"
                        left-icon="solar:refresh-bold-duotone"
                        class="tw-text-sm"
                    >
                        Clear
                    </a-button>

                    <!-- Bulk Actions -->
                    <div v-if="selectedProducts.length > 0" class="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
                        <span class="tw-text-sm tw-text-gray-600">{{ selectedProducts.length }} selected</span>
                        <a-button
                            @click="editSelected"
                            intent="secondary"
                            left-icon="solar:pen-bold-duotone"
                            class="tw-text-sm"
                        >
                            Edit
                        </a-button>
                        <a-button
                            @click="deleteSelected"
                            intent="danger"
                            left-icon="solar:trash-bin-2-bold-duotone"
                            class="tw-text-sm"
                        >
                            Delete
                        </a-button>
                    </div>
                </div>

                <!-- Content Area -->
                <div class="tw-flex-1">
                    <!-- Loading State -->
                    <div v-if="loading">
                        <!-- Grid View Skeleton -->
                        <div v-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 xl:tw-grid-cols-4 tw-gap-4">
                            <div
                                v-for="n in 8"
                                :key="n"
                                class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-animate-pulse"
                            >
                                <!-- Skeleton Checkbox and Actions -->
                                <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                    <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                    <div class="tw-flex tw-items-center tw-gap-1">
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                        <div class="tw-w-8 tw-h-8 tw-bg-gray-200 tw-rounded-md"></div>
                                    </div>
                                </div>

                                <!-- Skeleton Image -->
                                <div class="tw-w-full tw-h-32 tw-bg-gray-200 tw-rounded-lg tw-mb-3"></div>

                                <!-- Skeleton Product Info -->
                                <div>
                                    <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-3/4"></div>
                                    <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-mb-2 tw-w-full"></div>
                                    <div class="tw-flex tw-gap-1 tw-mt-2">
                                        <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                        <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- List View Skeleton -->
                        <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                            <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                                <thead class="tw-bg-gray-50">
                                    <tr>
                                        <th class="tw-px-4 tw-py-3 tw-text-left">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                                        </th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Image</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Title</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Description</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Categories</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Created</th>
                                        <th class="tw-px-4 tw-py-3 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                    <tr
                                        v-for="n in 12"
                                        :key="n"
                                        class="tw-animate-pulse"
                                    >
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-w-4 tw-h-4 tw-bg-gray-200 tw-rounded"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-w-16 tw-h-16 tw-bg-gray-200 tw-rounded-lg"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-48"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-flex tw-gap-1">
                                                <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-16"></div>
                                                <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-12"></div>
                                            </div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4">
                                            <div class="tw-h-3 tw-bg-gray-200 tw-rounded tw-w-20"></div>
                                        </td>
                                        <td class="tw-px-4 tw-py-4 tw-text-right">
                                            <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                                <div class="tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded"></div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="products.length === 0 && !searchQuery && !hasActiveFilters" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:box-linear" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No products yet</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Start by adding your first product</p>
                        <a-button
                            as="router-link"
                            :to="{ name: 'pages.company.products.create' }"
                            intent="primary"
                            left-icon="solar:add-circle-bold-duotone"
                        >
                            Add Product
                        </a-button>
                    </div>

                    <!-- No Search Results -->
                    <div v-else-if="products.length === 0" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center">
                        <icon icon="solar:box-bold-duotone" class="tw-text-6xl tw-text-gray-300 tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">No products found</h3>
                        <p class="tw-text-gray-600 tw-mb-4">Try adjusting your search or filters</p>
                        <a-button
                            @click="clearFilters"
                            intent="secondary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            Clear Filters
                        </a-button>
                    </div>

                    <!-- Grid View -->
                    <div v-else-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 xl:tw-grid-cols-4 tw-gap-4">
                        <div
                            v-for="product in products"
                            :key="product.id"
                            class="tw-group tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 hover:tw-shadow-md tw-transition-all tw-duration-200 tw-cursor-pointer"
                            @click="selectProduct(product)"
                        >
                            <!-- Selection Checkbox -->
                            <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
                                <input
                                    type="checkbox"
                                    :checked="selectedProducts.includes(product.id)"
                                    @click.stop="toggleProductSelection(product.id)"
                                    class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                />
                                <div class="tw-flex tw-items-center tw-gap-1 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                                    <button
                                        @click.stop="viewProduct(product)"
                                        class="tw-p-1.5 tw-text-sky-400 hover:tw-text-sky-600 tw-rounded-md hover:tw-bg-sky-100"
                                        title="View"
                                    >
                                        <icon icon="solar:eye-bold-duotone" class="tw-text-lg" />
                                    </button>
                                    <router-link
                                        @click.stop
                                        :to="{ name: 'pages.company.products.edit', params: { id: product.id } }"
                                        class="tw-p-1.5 tw-text-emerald-400 hover:tw-text-emerald-600 tw-rounded-md hover:tw-bg-emerald-100"
                                        title="Edit"
                                    >
                                        <icon icon="solar:pen-bold-duotone" class="tw-text-lg" />
                                    </router-link>

                                    <button
                                        @click.stop="deleteProduct(product)"
                                        class="tw-p-1.5 tw-text-rose-400 hover:tw-text-rose-600 tw-rounded-md hover:tw-bg-rose-100"
                                        title="Delete"
                                    >
                                        <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-lg" />
                                    </button>
                                </div>
                            </div>

                            <!-- Product Image -->
                            <div class="tw-flex tw-items-center tw-justify-center tw-h-32 tw-bg-gray-50 tw-rounded-lg tw-mb-3">
                                <img
                                    v-if="product.image"
                                    :src="product.image"
                                    :alt="product.title"
                                    class="tw-max-w-full tw-max-h-full tw-object-cover tw-rounded"
                                />
                                <icon
                                    v-else
                                    icon="solar:box-bold-duotone"
                                    class="tw-text-4xl tw-text-gray-400"
                                />
                            </div>

                            <!-- Product Info -->
                            <div>
                                <h4 class="tw-font-medium tw-text-gray-900 tw-truncate tw-mb-1" :title="product.title">{{ product.title }}</h4>
                                <p class="tw-text-sm tw-text-gray-600 tw-line-clamp-1 tw-mb-2">{{ product.description }}</p>
                                <div class="tw-flex tw-gap-1 tw-mt-2 tw-overflow-hidden">
                                    <div class="tw-flex tw-gap-1 tw-overflow-hidden tw-flex-1">
                                        <span
                                            v-for="category in product.categories.slice(0, 2)"
                                            :key="category.id"
                                            class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-primary-100 tw-text-primary-800 tw-min-w-0 tw-flex-shrink tw-overflow-hidden"
                                            :title="category.name"
                                        >
                                            <span class="tw-truncate">{{ category.name }}</span>
                                        </span>
                                    </div>
                                    <span
                                        v-if="product.categories.length > 2"
                                        class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800 tw-flex-shrink-0"
                                        :title="`${product.categories.length - 2} more categories`"
                                    >
                                        +{{ product.categories.length - 2 }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- List View -->
                    <div v-else class="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
                        <table class="tw-min-w-full tw-divide-y tw-divide-gray-200">
                            <thead class="tw-bg-gray-50">
                                <tr>
                                    <th class="tw-px-3 tw-py-2 tw-text-left">
                                        <input
                                            type="checkbox"
                                            :checked="allProductsSelected"
                                            @change="toggleAllProducts"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </th>
                                    <th class="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Image</th>
                                    <th class="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Title</th>
                                    <th class="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Description</th>
                                    <th class="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Categories</th>
                                    <th class="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Created</th>
                                    <th class="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody class="tw-bg-white tw-divide-y tw-divide-gray-200">
                                <tr
                                    v-for="product in products"
                                    :key="product.id"
                                    class="hover:tw-bg-gray-50"
                                >
                                    <td class="tw-px-3 tw-py-2">
                                        <input
                                            type="checkbox"
                                            :checked="selectedProducts.includes(product.id)"
                                            @change="toggleProductSelection(product.id)"
                                            class="tw-w-4 tw-h-4 tw-text-primary-600 tw-border-gray-300 tw-rounded focus:tw-ring-primary-500"
                                        />
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-w-12 tw-h-12 tw-bg-gray-50 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                                            <img
                                                v-if="product.image"
                                                :src="product.image"
                                                :alt="product.title"
                                                class="tw-w-full tw-h-full tw-object-cover tw-rounded-lg"
                                            />
                                            <icon
                                                v-else
                                                icon="solar:box-bold-duotone"
                                                class="tw-text-xl tw-text-gray-400"
                                            />
                                        </div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-text-sm tw-font-medium tw-text-gray-900">{{ product.title }}</div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-text-sm tw-text-gray-500 tw-max-w-xs tw-truncate">{{ product.description }}</div>
                                    </td>
                                    <td class="tw-px-3 tw-py-2">
                                        <div class="tw-flex tw-gap-1 tw-overflow-hidden">
                                            <div class="tw-flex tw-gap-1 tw-overflow-hidden tw-flex-1">
                                                <span
                                                    v-for="category in product.categories.slice(0, 2)"
                                                    :key="category.id"
                                                    class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-primary-100 tw-text-primary-800 tw-min-w-0 tw-flex-shrink tw-overflow-hidden"
                                                    :title="category.name"
                                                >
                                                    <span class="tw-truncate">{{ category.name }}</span>
                                                </span>
                                            </div>
                                    <span
                                        v-if="product.categories.length > 2"
                                        class="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800 tw-flex-shrink-0"
                                        :title="`${product.categories.length - 2} more categories`"
                                    >
                                        +{{ product.categories.length - 2 }}
                                    </span>
                                </div>
                            </td>
                                    <td class="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-500">{{ formatDate(product.createdDate) }}</td>
                                    <td class="tw-px-3 tw-py-2 tw-text-right tw-text-sm tw-font-medium">
                                        <div class="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-w-fit">
                                            <button
                                                @click="viewProduct(product)"
                                                class="tw-p-1 tw-text-sky-400 hover:tw-text-sky-600 tw-rounded-md hover:tw-bg-sky-100"
                                                title="View"
                                            >
                                                <icon icon="solar:eye-bold-duotone" class="tw-text-base" />
                                            </button>
                                            <router-link
                                                @click.stop
                                                :to="{ name: 'pages.company.products.edit', params: { id: product.id } }"
                                                class="tw-p-1 tw-text-emerald-400 hover:tw-text-emerald-600 tw-rounded-md hover:tw-bg-emerald-100"
                                                title="Edit"
                                            >
                                                <icon icon="solar:pen-bold-duotone" class="tw-text-base" />
                                            </router-link>
                                            <button
                                                @click="deleteProduct(product)"
                                                class="tw-p-1 tw-text-rose-400 hover:tw-text-rose-600 tw-rounded-md hover:tw-bg-rose-100"
                                                title="Delete"
                                            >
                                                <icon icon="solar:trash-bin-2-bold-duotone" class="tw-text-base" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="tw-flex tw-items-center tw-justify-between tw-mt-6">
                        <div class="tw-text-sm tw-text-gray-600">
                            Showing {{ paginationData.from }} to {{ paginationData.to }} of {{ paginationData.total }} products
                        </div>
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <a-button
                                @click="currentPage--; loadData()"
                                :disabled="currentPage === 1"
                                intent="light"
                                left-icon="solar:arrow-left-linear"
                            >
                                Previous
                            </a-button>
                            <a-button
                                @click="currentPage++; loadData()"
                                :disabled="currentPage === totalPages"
                                intent="light"
                                right-icon="solar:arrow-right-linear"
                            >
                                Next
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>




        <!-- Delete Confirmation Modal -->
        <DeleteModal
            :visible="deleteModalVisible"
            :loading="deletingProduct"
            @confirm="confirmDelete"
            @cancel="deleteModalVisible = false"
            :title="deleteModalTitle"
            :description="deleteModalDescription"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import DeleteModal from '@/components/modals/DeleteModal.vue'
import FormField from '@/components/form/FormField.vue'
import FormMultiSelect from '@/components/form/FormMultiSelect.vue'
import productApi from '@/api/products'
import useAlert from '@/composables/useAlert'

const router = useRouter()

// Data
const loading = ref(false)
const statsLoading = ref(false)
const searchQuery = ref('')
const viewMode = ref('grid')
const currentPage = ref(1)
const itemsPerPage = ref(12)
const selectedProducts = ref([])
const selectedCategories = ref([])
const deleteModalVisible = ref(false)
const deletingProduct = ref(false)
const productToDelete = ref(null)

// Sort and filters
const sortBy = ref('name')
const categories = ref([])

// API data
const products = ref([])
const paginationData = ref({
    current_page: 1,
    last_page: 1,
    per_page: 12,
    total: 0,
    from: 0,
    to: 0
})

// Computed properties
const hasActiveFilters = computed(() => {
    return selectedCategories.value.length > 0 || searchQuery.value
})

const totalPages = computed(() => {
    return paginationData.value.last_page
})

const allProductsSelected = computed(() => {
    return products.value.length > 0 && 
           products.value.every(product => selectedProducts.value.includes(product.id))
})

const deleteModalTitle = computed(() => {
    if (selectedProducts.value.length > 1) {
        return `Delete ${selectedProducts.value.length} products?`
    }
    return 'Delete product?'
})

const deleteModalDescription = computed(() => {
    if (selectedProducts.value.length > 1) {
        return 'This action cannot be undone. All selected products will be permanently deleted.'
    }
    return 'This action cannot be undone. The product will be permanently deleted.'
})

const categoryOptions = computed(() => {
    return categories.value.map(cat => ({
        id: cat.id,
        name: cat.name
    }))
})

// Methods
const clearFilters = () => {
    selectedCategories.value = []
    searchQuery.value = ''
    currentPage.value = 1
    loadData()
}

const toggleProductSelection = (productId) => {
    const index = selectedProducts.value.indexOf(productId)
    if (index > -1) {
        selectedProducts.value.splice(index, 1)
    } else {
        selectedProducts.value.push(productId)
    }
}

const toggleAllProducts = () => {
    if (allProductsSelected.value) {
        selectedProducts.value = selectedProducts.value.filter(id =>
            !products.value.some(product => product.id === id)
        )
    } else {
        products.value.forEach(product => {
            if (!selectedProducts.value.includes(product.id)) {
                selectedProducts.value.push(product.id)
            }
        })
    }
}

const selectProduct = (product) => {
    viewProduct(product)
}

const viewProduct = (product) => {
    router.push({ 
        name: 'pages.company.products.show', 
        params: { id: product.id } 
    })
}

const deleteProduct = (product) => {
    productToDelete.value = product
    selectedProducts.value = [product.id]
    deleteModalVisible.value = true
}

const editSelected = () => {
    if (selectedProducts.value.length === 1) {
        const productId = selectedProducts.value[0]
        router.push({ name: 'pages.company.products.edit', params: { id: productId } })
    }
}

const deleteSelected = () => {
    deleteModalVisible.value = true
}

const confirmDelete = async () => {
    deletingProduct.value = true
    
    try {
        if (selectedProducts.value.length === 1) {
            // Single product delete
            const productId = selectedProducts.value[0]
            await productApi.destroy(productId)
            useAlert('Product deleted successfully', 'success')
        } else if (selectedProducts.value.length > 1) {
            // Multiple products delete
            await productApi.destroyMultiple(selectedProducts.value)
            useAlert(`${selectedProducts.value.length} products deleted successfully`, 'success')
        }
        
        // Remove deleted products from the list and reload data
        await loadData()
        selectedProducts.value = []
        
    } catch (error) {
        console.error('Error deleting product(s):', error)
        if (error.response?.status === 404) {
            useAlert('Product not found or already deleted', 'warning')
            await loadData() // Refresh the list
        } else if (error.response?.status === 403) {
            useAlert('You do not have permission to delete this product', 'danger')
        } else {
            useAlert('Failed to delete product(s). Please try again.', 'danger')
        }
    } finally {
        deletingProduct.value = false
        deleteModalVisible.value = false
        productToDelete.value = null
    }
}

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
}

const fetchCategories = async () => {
    try {
        // In a real app, this would fetch from the API
        // const response = await categoriesApi.admin.paginate({ type: 'product' })
        // categories.value = response.data.data
        categories.value = []
    } catch (error) {
        console.error('Error fetching categories:', error)
        useAlert('Failed to load categories', 'danger')
    }
}

const loadData = async () => {
    loading.value = true
    statsLoading.value = true

    try {
        const params = {
            page: currentPage.value,
            per_page: itemsPerPage.value
        }

        // Add search parameter if search query exists
        if (searchQuery.value) {
            params.search = searchQuery.value
        }

        // Add category filter if selected
        if (selectedCategories.value.length > 0) {
            params.category_ids = selectedCategories.value.map(cat => cat.id).join(',')
        }

        // Add sort parameter
        if (sortBy.value) {
            params.sort = sortBy.value
        }

        const response = await productApi.paginate(params)
        
        if (response.data && response.data.data) {
            // Map API response to component structure
            products.value = response.data.data.data.map(product => {
                console.log(`Product ${product.id} categories:`, product.categories, 'Length:', product.categories?.length)
                return {
                    id: product.id,
                    title: product.name,
                    description: product.description,
                    image: product.thumbnail_url,
                    createdDate: product.created_at,
                    categories: product.categories || []
                }
            })

            // Update pagination data
            paginationData.value = {
                current_page: response.data.data.current_page,
                last_page: response.data.data.last_page,
                per_page: response.data.data.per_page,
                total: response.data.data.total,
                from: response.data.data.from,
                to: response.data.data.to
            }
        }

    } catch (error) {
        console.error('Error loading data:', error)
        useAlert('Failed to load products', 'danger')
        products.value = []
    } finally {
        loading.value = false
        statsLoading.value = false
    }
}

// Watch for search query changes
const debouncedSearch = ref(null)
const handleSearch = () => {
    if (debouncedSearch.value) {
        clearTimeout(debouncedSearch.value)
    }
    debouncedSearch.value = setTimeout(() => {
        currentPage.value = 1
        loadData()
    }, 500)
}

// Watchers for filters
watch(selectedCategories, () => {
    currentPage.value = 1
    loadData()
}, { deep: true })

watch(sortBy, () => {
    currentPage.value = 1
    loadData()
})

// Initialize
onMounted(() => {
    fetchCategories()
    loadData()
})
</script>

<style scoped>
.tw-line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>